// src/pages/admin/AdminUserMerge.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSearch, FaExchangeAlt, FaArrowRight, FaTrashAlt, FaCheckCircle, FaExclamationTriangle, FaClipboardList } from 'react-icons/fa';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import classNames from 'classnames';

interface MergePreviewUser {
  id: number;
  email?: string | null;
  phone_number?: string | null;
  name?: string | null;
  nickname?: string | null;
  chart_number?: string | null;
  provider?: string | null;
  wechat_openid?: string | null;
  wechat_unionid?: string | null;
  line_user_id?: string | null;
  reservation_count: number;
  questionnaire_count: number;
  profile?: any | null;
}

const AdminUserMerge: React.FC = () => {
  const { t } = useTranslation();
  
  // Selection State
  const [sourceSearch, setSourceSearch] = useState('');
  const [targetSearch, setTargetSearch] = useState('');
  const [sourceResults, setSourceResults] = useState<any[]>([]);
  const [targetResults, setTargetResults] = useState<any[]>([]);
  const [selectedSource, setSelectedSource] = useState<MergePreviewUser | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<MergePreviewUser | null>(null);

  // Comparison State
  const [comparison, setComparison] = useState<{ source: MergePreviewUser; target: MergePreviewUser } | null>(null);
  const [overrides, setOverrides] = useState<Record<string, 'source' | 'target'>>({});
  const [confirmationPhrase, setConfirmationPhrase] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMerging, setIsMerging] = useState(false);

  // Search Logic
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (sourceSearch.length > 0) handleSearch(sourceSearch, 'source');
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [sourceSearch]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (targetSearch.length > 0) handleSearch(targetSearch, 'target');
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [targetSearch]);

  const handleSearch = async (q: string, type: 'source' | 'target') => {
    try {
      const res = await api.get('/admin/users/merge/search', { params: { q } });
      if (type === 'source') setSourceResults(res.data);
      else setTargetResults(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchComparison = async () => {
    if (!selectedSource || !selectedTarget) return;
    if (selectedSource.id === selectedTarget.id) {
      toast.error(t('admin.merge_same_user_error'));
      return;
    }
    try {
      setLoading(true);
      const res = await api.get('/admin/users/merge/compare', {
        params: { source_id: selectedSource.id, target_id: selectedTarget.id }
      });
      setComparison(res.data);
      // Initialize overrides: default to target wins
      const initialOverrides: Record<string, 'source' | 'target'> = {};
      ['name', 'nickname', 'phone_number', 'chart_number', 'email'].forEach(f => {
        initialOverrides[f] = 'target';
      });
      setOverrides(initialOverrides);
    } catch (err) {
      toast.error(t('admin.fetch_comparison_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async () => {
    if (confirmationPhrase !== 'MERGE') {
      toast.error(t('admin.invalid_confirmation_phrase'));
      return;
    }
    try {
      setIsMerging(true);
      await api.post('/admin/users/merge/execute', {
        source_id: comparison?.source.id,
        target_id: comparison?.target.id,
        overrides,
        confirmation_phrase: confirmationPhrase
      });
      toast.success(t('admin.merge_success'));
      setComparison(null);
      setSelectedSource(null);
      setSelectedTarget(null);
      setConfirmationPhrase('');
    } catch (err) {
      toast.error(t('admin.merge_failed'));
    } finally {
      setIsMerging(false);
    }
  };

  const ComparisonRow = ({ label, field, sourceVal, targetVal }: { label: string, field: string, sourceVal: any, targetVal: any }) => {
    const isDifferent = sourceVal !== targetVal && sourceVal && targetVal;
    return (
      <tr className="border-b hover:bg-gray-50 transition-colors">
        <td className="p-3 font-semibold text-gray-600 bg-gray-50 w-32">{label}</td>
        <td className={classNames("p-3 w-1/3", overrides[field] === 'source' ? "bg-amber-50" : "")}>
          <div className="flex items-center justify-between">
            <span className="break-all">{sourceVal || '-'}</span>
            {isDifferent && (
              <input 
                type="radio" 
                name={field} 
                checked={overrides[field] === 'source'} 
                onChange={() => setOverrides({...overrides, [field]: 'source'})}
                className="ml-2 accent-coral"
              />
            )}
          </div>
        </td>
        <td className="p-3 text-center text-gray-400 w-16">
          <FaExchangeAlt />
        </td>
        <td className={classNames("p-3 w-1/3", overrides[field] === 'target' ? "bg-emerald-50" : "")}>
          <div className="flex items-center justify-between">
            <span className="break-all">{targetVal || '-'}</span>
            {isDifferent && (
              <input 
                type="radio" 
                name={field} 
                checked={overrides[field] === 'target'} 
                onChange={() => setOverrides({...overrides, [field]: 'target'})}
                className="ml-2 accent-emerald-600"
              />
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <header className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-navy font-outfit flex items-center gap-3">
            <FaExchangeAlt /> {t('admin.menu.user_merge')}
          </h1>
          <p className="text-gray-500 mt-2">
            {t('admin.merge_page_desc')}
          </p>
        </div>
      </header>

      {!comparison ? (
        <div className="grid md:grid-cols-2 gap-8">
          {/* Source Selection */}
          <section className="bg-white p-6 rounded-3xl shadow-soft border border-gray-100 flex flex-col">
            <h3 className="text-xl font-bold text-red-600 mb-4 flex items-center gap-2">
              <FaTrashAlt /> 1. {t('admin.merge_source_select')}
            </h3>
            <div className="relative mb-4">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={t('admin.search_placeholder')}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 rounded-2xl border-none focus:ring-2 focus:ring-coral transition-all"
                value={sourceSearch}
                onChange={(e) => setSourceSearch(e.target.value)}
              />
            </div>
            {selectedSource ? (
              <div className="p-4 bg-red-50 rounded-2xl border border-red-100 flex items-center justify-between">
                <div>
                  <div className="font-bold text-navy">{selectedSource.name || selectedSource.nickname || t('admin.user_no_name')}</div>
                  <div className="text-sm text-gray-500">{selectedSource.email || selectedSource.phone_number || t('admin.user_no_contact')}</div>
                </div>
                <button onClick={() => setSelectedSource(null)} className="text-red-500 hover:scale-110 transition-transform">✕</button>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto max-h-[300px] space-y-2 mt-2 custom-scrollbar">
                {sourceResults.map(u => (
                  <div 
                    key={u.id} 
                    onClick={() => setSelectedSource(u)}
                    className="p-3 hover:bg-gray-50 rounded-xl cursor-pointer border border-transparent hover:border-coral/20 transition-all flex items-center justify-between group"
                  >
                    <div>
                      <div className="font-semibold text-navy group-hover:text-coral transition-colors">{u.name || u.nickname || '-'}</div>
                      <div className="text-xs text-gray-400">{u.email || u.phone_number || '-'}</div>
                    </div>
                    <div className="text-xs text-gray-400">ID: {u.id}</div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Target Selection */}
          <section className="bg-white p-6 rounded-3xl shadow-soft border border-gray-100 flex flex-col">
            <h3 className="text-xl font-bold text-emerald-600 mb-4 flex items-center gap-2">
              <FaCheckCircle /> 2. {t('admin.merge_target_select')}
            </h3>
            <div className="relative mb-4">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={t('admin.search_placeholder')}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 rounded-2xl border-none focus:ring-2 focus:ring-emerald-500 transition-all"
                value={targetSearch}
                onChange={(e) => setTargetSearch(e.target.value)}
              />
            </div>
            {selectedTarget ? (
              <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-100 flex items-center justify-between">
                <div>
                  <div className="font-bold text-navy">{selectedTarget.name || selectedTarget.nickname || t('admin.user_no_name')}</div>
                  <div className="text-sm text-gray-500">{selectedTarget.email || selectedTarget.phone_number || t('admin.user_no_contact')}</div>
                </div>
                <button onClick={() => setSelectedTarget(null)} className="text-emerald-500 hover:scale-110 transition-transform">✕</button>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto max-h-[300px] space-y-2 mt-2 custom-scrollbar">
                {targetResults.map(u => (
                  <div 
                    key={u.id} 
                    onClick={() => setSelectedTarget(u)}
                    className="p-3 hover:bg-gray-50 rounded-xl cursor-pointer border border-transparent hover:border-emerald-500/20 transition-all flex items-center justify-between group"
                  >
                    <div>
                      <div className="font-semibold text-navy group-hover:text-emerald-600 transition-colors">{u.name || u.nickname || '-'}</div>
                      <div className="text-xs text-gray-400">{u.email || u.phone_number || '-'}</div>
                    </div>
                    <div className="text-xs text-gray-400">ID: {u.id}</div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="md:col-span-2 flex justify-center mt-4">
            <button
              onClick={fetchComparison}
              disabled={!selectedSource || !selectedTarget || selectedSource.id === selectedTarget.id || loading}
              className={classNames(
                "px-10 py-4 rounded-full font-bold text-lg shadow-soft transition-all active:scale-95 flex items-center gap-3",
                (!selectedSource || !selectedTarget || selectedSource.id === selectedTarget.id) 
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed" 
                  : "bg-navy text-white hover:bg-navy/90 hover:shadow-card"
              )}
            >
              {loading ? t('loading') : <><FaExchangeAlt /> {t('admin.start_compare')}</>}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-3xl shadow-card p-6 md:p-8 border border-gray-100 animate-in zoom-in-95 duration-300">
          <div className="flex items-center justify-between mb-8 pb-4 border-b">
            <h2 className="text-2xl font-extrabold text-navy">{t('admin.merge_review_title')}</h2>
            <button onClick={() => setComparison(null)} className="text-gray-400 hover:text-navy transition-colors">{t('back')}</button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-navy text-white">
                  <th className="p-3 text-sm rounded-tl-xl">{t('admin.merge_field')}</th>
                  <th className="p-3 text-sm text-center">
                    <div className="flex flex-col items-center">
                      <span className="text-red-300">SOURCE ({comparison.source.id})</span>
                      <span className="text-[10px] opacity-70">WILL BE DELETED</span>
                    </div>
                  </th>
                  <th className="p-3 text-sm"></th>
                  <th className="p-3 text-sm text-center rounded-tr-xl">
                    <div className="flex flex-col items-center">
                      <span className="text-emerald-300">TARGET ({comparison.target.id})</span>
                      <span className="text-[10px] opacity-70">WILL BE KEPT</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <ComparisonRow label={t('profile.full_name')} field="name" sourceVal={comparison.source.name} targetVal={comparison.target.name} />
                <ComparisonRow label={t('profile.nickname')} field="nickname" sourceVal={comparison.source.nickname} targetVal={comparison.target.nickname} />
                <ComparisonRow label="Email" field="email" sourceVal={comparison.source.email} targetVal={comparison.target.email} />
                <ComparisonRow label={t('auth.phone_number')} field="phone_number" sourceVal={comparison.source.phone_number} targetVal={comparison.target.phone_number} />
                <ComparisonRow label={t('doctor.chart_number')} field="chart_number" sourceVal={comparison.source.chart_number} targetVal={comparison.target.chart_number} />
                
                {/* Fixed Transfer Sections */}
                <tr className="bg-gray-50/50">
                  <td className="p-3 font-semibold text-gray-600">{t('admin.merge_unified_assets')}</td>
                  <td colSpan={3} className="p-3">
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-100">
                        <FaClipboardList className="text-coral" /> 
                        <span className="font-bold text-coral">{comparison.source.reservation_count}</span> {t('admin.menu.reservations')}
                        <FaArrowRight className="text-gray-300 mx-1" />
                        <span className="text-gray-500 font-bold">{comparison.target.reservation_count + comparison.source.reservation_count}</span>
                      </div>
                      <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-100">
                        <FaClipboardList className="text-coral" /> 
                        <span className="font-bold text-coral">{comparison.source.questionnaire_count}</span> {t('admin.menu.questionnaires')}
                        <FaArrowRight className="text-gray-300 mx-1" />
                        <span className="text-gray-500 font-bold">{comparison.target.questionnaire_count + comparison.source.questionnaire_count}</span>
                      </div>
                    </div>
                  </td>
                </tr>

                {/* Identity Unification */}
                <tr className="bg-gray-50/50">
                  <td className="p-3 font-semibold text-gray-600">{t('admin.merge_identity')}</td>
                  <td colSpan={3} className="p-3">
                    <div className="space-y-1 text-xs text-gray-500">
                      {comparison.source.wechat_openid && !comparison.target.wechat_openid && (
                        <div className="flex items-center gap-2 text-emerald-600 font-semibold">
                           <FaCheckCircle size={10} /> WeChat OpenID will be added to Target.
                        </div>
                      )}
                      {comparison.source.wechat_unionid && !comparison.target.wechat_unionid && (
                        <div className="flex items-center gap-2 text-emerald-600 font-semibold">
                           <FaCheckCircle size={10} /> WeChat UnionID will be unified.
                        </div>
                      )}
                      {comparison.source.line_user_id && !comparison.target.line_user_id && (
                        <div className="flex items-center gap-2 text-emerald-600 font-semibold">
                           <FaCheckCircle size={10} /> LINE User ID will be moved.
                        </div>
                      )}
                      {(!comparison.source.wechat_openid && !comparison.source.line_user_id) && <div>No social IDs to unify.</div>}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-12 p-8 bg-amber-50 rounded-3xl border-2 border-dashed border-amber-200">
            <h4 className="flex items-center gap-2 text-amber-700 font-bold mb-4">
              <FaExclamationTriangle className="text-xl" /> {t('admin.merge_danger_zone')}
            </h4>
            <p className="text-sm text-amber-600 mb-6 leading-relaxed">
              {t('admin.merge_consequences')}
            </p>
            
            <div className="max-w-md">
              <label className="block text-xs font-bold text-amber-700 mb-2 uppercase tracking-wider">
                {t('admin.type_merge_confirm')}
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="MERGE"
                  className="flex-1 px-4 py-3 rounded-2xl border-none focus:ring-2 focus:ring-amber-500 bg-white shadow-soft"
                  value={confirmationPhrase}
                  onChange={(e) => setConfirmationPhrase(e.target.value)}
                />
                <button
                  onClick={handleMerge}
                  disabled={confirmationPhrase !== 'MERGE' || isMerging}
                  className={classNames(
                    "px-8 py-3 rounded-2xl font-bold transition-all shadow-soft active:scale-95",
                    (confirmationPhrase === 'MERGE' && !isMerging)
                      ? "bg-red-600 text-white hover:bg-red-700 hover:shadow-card"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  )}
                >
                  {isMerging ? t('processing') : t('admin.finalize_merge')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserMerge;
